/**
 * Filtro specializzato per array di valori
 *
 * @description
 * ArrayFilter gestisce array di valori primitivi (stringhe, numeri) per
 * operazioni come IN, multiple selection, tag selection, etc.
 * Supporta serializzazione automatica con separatori personalizzati.
 */

import { BaseFilter } from '../core/base-filter';
import type { QueryParams, IdConstructor } from '../types';

/**
 * Interfaccia per dati di filtro array
 * Utilizzabile per Vue 3 Composition API con oggetti plain reattivi
 */
export interface IArrayFilterData<T extends string | number = string> {
	/** Nome della proprietà da filtrare */
	propertyName: string;
	/** Valore corrente del filtro (array di valori) */
	value: T[] | null;
	/** Valore di default del filtro */
	defaultValue: T[] | null;
	/** Nome della proprietà nella query string */
	queryPropertyName: string;
	/** Operatore di ricerca applicato */
	searchOperator: string;
	/** Se aggiornare la query string al cambio */
	updateQueryOnChange: boolean;
	/** Separatore per serializzazione (default: '*') */
	separator: string;
}

/**
 * Tipo combinato per filtro array con metodi fluenti
 */
export type ArrayFilterFactory<T extends string | number = string> = IArrayFilterData<T> & {
	/** Imposta il valore iniziale */
	initialValue(value: T[] | null): ArrayFilterFactory<T>;
	/** Imposta il valore di default */
	default(value: T[] | null): ArrayFilterFactory<T>;
	/** Configura aggiornamento query string */
	updateQueryOnFilterChange(value: boolean): ArrayFilterFactory<T>;
	/** Configura nome proprietà query string */
	setQueryPropertyName(value: string): ArrayFilterFactory<T>;
	/** Operatore "in" (WHERE field IN (val1, val2, ...)) */
	in(): ArrayFilterFactory<T>;
	/** Operatore "contiene" */
	contains(): ArrayFilterFactory<T>;
	/** Configura separatore personalizzato */
	setSeparator(separator: string): ArrayFilterFactory<T>;
	/** Aggiunge un valore all'array */
	addValue(value: T): ArrayFilterFactory<T>;
	/** Rimuove un valore dall'array */
	removeValue(value: T): ArrayFilterFactory<T>;
};

/**
 * Filtro per array di valori primitivi
 *
 * @description
 * ArrayFilter è ottimizzato per gestire selezioni multiple, operazioni IN,
 * e qualsiasi scenario dove si lavora con array di valori primitivi.
 * Serializza automaticamente gli array come stringhe separate.
 *
 * @example
 * // Array di stringhe per categorie
 * const categoriesFilter = new ArrayFilter('categories', String)
 *   .in()                          // WHERE categories IN ('cat1', 'cat2', ...)
 *   .initialValue(['electronics', 'books']);
 *
 * @example
 * // Array di numeri per multiple IDs
 * const idsFilter = new ArrayFilter('productIds', Number)
 *   .in()                          // WHERE productIds IN (1, 2, 3, ...)
 *   .initialValue([1, 2, 3, 4]);
 *
 * @example
 * // Uso con Element UI multiple select
 * <el-select
 *   v-model="dataFilters.categories.value"
 *   multiple
 *   placeholder="Seleziona categorie"
 * >
 *   <el-option
 *     v-for="category in availableCategories"
 *     :key="category.id"
 *     :label="category.name"
 *     :value="category.id"
 *   />
 * </el-select>
 *
 * @example
 * // Per tag dinamici
 * const tagsFilter = new ArrayFilter('tags', String)
 *   .contains()                    // Ricerca che contiene uno dei tag
 *   .default([])                   // Array vuoto di default
 *   .updateQueryOnFilterChange(true);
 *
 * @template T - Tipo degli elementi dell'array (string | number)
 * @category Filters
 * @since 1.0.0
 */
export class ArrayFilter<T extends string | number = string> extends BaseFilter<T[] | null> {
	/**
	 * Tipo degli elementi interni dell'array
	 * @private
	 */
	private innerType: IdConstructor = String;

	/**
	 * Separatore per la serializzazione (default: '*')
	 * @private
	 */
	private separator: string = '*';

	/**
	 * Crea una nuova istanza di ArrayFilter
	 *
	 * @param propertyName - Nome della proprietà da filtrare
	 * @param innerType - Tipo degli elementi dell'array (String o Number)
	 * @param separator - Separatore per serializzazione (default: '*')
	 *
	 * @example
	 * // Array di stringhe
	 * const filter = new ArrayFilter('categories', String);
	 *
	 * @example
	 * // Array di numeri
	 * const filter = new ArrayFilter('productIds', Number);
	 *
	 * @example
	 * // Con separatore personalizzato
	 * const filter = new ArrayFilter('tags', String, ',');
	 *
	 * @public
	 */
	constructor(propertyName: string, innerType: IdConstructor = String, separator: string = '*') {
		super(Array, propertyName);
		this.innerType = innerType;
		this.separator = separator;
	}

	/**
	 * Deserializza il valore dalla query string
	 * Converte stringhe separate in array del tipo appropriato
	 *
	 * @param query - Parametri della query string
	 *
	 * @example
	 * // URL: ?categories=electronics*books*music
	 * filter._fromQuery({ categories: 'electronics*books*music' });
	 * // filter.value diventa ['electronics', 'books', 'music']
	 *
	 * @example
	 * // URL: ?product_ids=1*2*3*4
	 * filter._fromQuery({ product_ids: '1*2*3*4' });
	 * // filter.value diventa [1, 2, 3, 4] (se innerType è Number)
	 *
	 * @protected
	 * @override
	 */
	protected _fromQuery(query: QueryParams): void {
		const value = query[this._queryPropertyName];
		if (value !== undefined) {
			// FIX: Gestisci 'null' letterale dalla query string
			if (value === 'null') {
				this.value = null;
				return;
			}

			if (!value) {
				this.value = this._defaultValue || null;
			} else {
				try {
					// Gestisce sia stringhe che possibili array dalla query
					const stringValue = String(value);
					const tokens = stringValue
						.split(this.separator)
						.map((item) => this._parseType(item.trim()))
						.filter((item) => item !== null);

					this.value = tokens.length > 0 ? (tokens as T[]) : this._defaultValue || null;
				} catch (error) {
					console.error('❌ ERROR array-filter.if:', error);
					console.warn(`ArrayFilter: Errore parsing valore "${value}":`, error);
					this.value = this._defaultValue || null;
				}
			}
		}
	}

	/**
	 * Converte un singolo elemento nel tipo appropriato
	 *
	 * @param value - Valore da convertire
	 * @returns Valore convertito o null se non valido
	 *
	 * @example
	 * // Con innerType = Number
	 * filter._parseType('123');  // 123
	 * filter._parseType('abc');  // null (non valido)
	 *
	 * // Con innerType = String
	 * filter._parseType('hello'); // 'hello'
	 * filter._parseType('');      // '' (stringa vuota valida)
	 *
	 * @private
	 */
	private _parseType(value: string): T | null {
		if (this.innerType === String) {
			// Le stringhe sono sempre valide (anche vuote)
			return value as T;
		} else if (this.innerType === Number) {
			const numValue = Number(value);
			// Ritorna null per NaN, altrimenti il numero
			return !isNaN(numValue) ? (numValue as T) : null;
		}
		return null;
	}

	/**
	 * Serializza l'array per la query string
	 * Converte l'array in stringa con il separatore specificato
	 *
	 * @returns Array serializzato come stringa
	 *
	 * @example
	 * filter.value = ['electronics', 'books', 'music'];
	 * filter._getSerializedValue(); // 'electronics*books*music'
	 *
	 * @example
	 * filter.value = [1, 2, 3, 4];
	 * filter._getSerializedValue(); // '1*2*3*4'
	 *
	 * @protected
	 * @override
	 */
	protected _getSerializedValue(): string | undefined {
		if (this.value && Array.isArray(this.value) && this.value.length > 0) {
			try {
				return this.value.join(this.separator);
			} catch (error) {
				console.error('❌ ERROR array-filter.Number:', error);
				console.warn('ArrayFilter: Errore serializzazione array:', error);
				return undefined;
			}
		}
		return undefined;
	}

	/**
	 * Verifica se il valore è quello di default
	 * Considera array vuoti come valori di default
	 *
	 * @returns true se il valore è di default, false altrimenti
	 *
	 * @example
	 * filter.value = [];
	 * filter._valueIsDefault(); // true
	 *
	 * filter.value = ['item'];
	 * filter._valueIsDefault(); // false
	 *
	 * @protected
	 * @override
	 */
	protected _valueIsDefault(): boolean {
		return (
			this.value === undefined ||
			this.value === null ||
			(Array.isArray(this.value) && this.value.length === 0) ||
			this.value === this._defaultValue
		);
	}

	/**
	 * Override del metodo _setContext per gestire array vuoti
	 * Non aggiunge il filtro al contesto se l'array è vuoto
	 *
	 * @param context - Contesto OData da modificare
	 *
	 * @protected
	 * @override
	 */
	protected _setContext(context: any): void {
		// Non aggiunge il filtro se l'array è vuoto o null
		if (!this.value || !Array.isArray(this.value) || this.value.length === 0) {
			return;
		}

		// Delega alla implementazione della classe base
		super._setContext(context);
	}

	/**
	 * Aggiunge un elemento all'array
	 *
	 * @param item - Elemento da aggiungere
	 * @returns true se l'elemento è stato aggiunto, false se già presente
	 *
	 * @example
	 * filter.addItem('electronics');
	 * // Se non presente: filter.value = [...existing, 'electronics']
	 *
	 * @public
	 */
	public addItem(item: T): boolean {
		if (!this.value) {
			this.value = [item];
			return true;
		}

		if (Array.isArray(this.value) && !this.value.includes(item)) {
			this.value = [...this.value, item];
			return true;
		}

		return false; // Elemento già presente
	}

	/**
	 * Rimuove un elemento dall'array
	 *
	 * @param item - Elemento da rimuovere
	 * @returns true se l'elemento è stato rimosso, false se non presente
	 *
	 * @example
	 * filter.removeItem('electronics');
	 * // Se presente: rimuove 'electronics' dall'array
	 *
	 * @public
	 */
	public removeItem(item: T): boolean {
		if (!this.value || !Array.isArray(this.value)) {
			return false;
		}

		const index = this.value.indexOf(item);
		if (index !== -1) {
			this.value = this.value.filter((existingItem) => existingItem !== item);
			return true;
		}

		return false; // Elemento non presente
	}

	/**
	 * Verifica se l'array contiene un elemento specifico
	 *
	 * @param item - Elemento da cercare
	 * @returns true se presente, false altrimenti
	 *
	 * @example
	 * if (filter.hasItem('electronics')) {
	 *   console.log('Electronics è selezionato');
	 * }
	 *
	 * @public
	 */
	public hasItem(item: T): boolean {
		return Boolean(this.value && Array.isArray(this.value) && this.value.includes(item));
	}

	/**
	 * Alterna la presenza di un elemento nell'array
	 *
	 * @param item - Elemento da alternare
	 * @returns true se l'elemento è ora presente, false se è stato rimosso
	 *
	 * @example
	 * filter.toggleItem('electronics');
	 * // Se presente: lo rimuove. Se assente: lo aggiunge
	 *
	 * @public
	 */
	public toggleItem(item: T): boolean {
		if (this.hasItem(item)) {
			this.removeItem(item);
			return false;
		} else {
			this.addItem(item);
			return true;
		}
	}

	/**
	 * Pulisce completamente l'array
	 *
	 * @example
	 * filter.clear(); // filter.value = []
	 *
	 * @public
	 */
	public clear(): void {
		this.value = [];
	}

	/**
	 * Restituisce il numero di elementi nell'array
	 *
	 * @returns Numero di elementi
	 *
	 * @example
	 * console.log(`Selezionati: ${filter.count()} elementi`);
	 *
	 * @public
	 */
	public count(): number {
		return this.value && Array.isArray(this.value) ? this.value.length : 0;
	}

	/**
	 * Verifica se l'array è vuoto
	 *
	 * @returns true se vuoto, false altrimenti
	 *
	 * @example
	 * if (filter.isEmpty()) {
	 *   showMessage('Nessun filtro attivo');
	 * }
	 *
	 * @public
	 */
	public isEmpty(): boolean {
		return this.count() === 0;
	}

	/**
	 * Restituisce una copia dell'array per sicurezza
	 *
	 * @returns Copia dell'array corrente
	 *
	 * @example
	 * const selectedItems = filter.getItems();
	 * // Manipolare selectedItems non influisce sul filtro
	 *
	 * @public
	 */
	public getItems(): T[] {
		return this.value && Array.isArray(this.value) ? [...this.value] : [];
	}

	/**
	 * Imposta tutti gli elementi in una volta
	 *
	 * @param items - Array di elementi da impostare
	 *
	 * @example
	 * filter.setItems(['electronics', 'books', 'music']);
	 *
	 * @public
	 */
	public setItems(items: T[]): void {
		if (Array.isArray(items)) {
			// Filtra elementi validi
			const validItems = items
				.map((item) => this._parseType(String(item)))
				.filter((item) => item !== null) as T[];
			this.value = validItems;
		} else {
			this.value = [];
		}
	}

	/**
	 * Cambia il separatore per la serializzazione
	 *
	 * @param newSeparator - Nuovo separatore
	 *
	 * @example
	 * filter.setSeparator(','); // Usa virgola invece di asterisco
	 *
	 * @public
	 */
	public setSeparator(newSeparator: string): void {
		this.separator = newSeparator || '*';
	}

	/**
	 * Restituisce il separatore corrente
	 *
	 * @returns Separatore attuale
	 *
	 * @public
	 */
	public getSeparator(): string {
		return this.separator;
	}
}

/**
 * Factory function per creare filtri array tipizzati
 * Compatibile con Vue 3 Composition API e completamente reattivo
 *
 * @param propertyName - Nome della proprietà da filtrare
 * @param type - Tipo degli elementi (String o Number)
 * @returns Oggetto filtro array tipizzato con API fluente
 *
 * @example
 * // Per Vue 3 Composition API con stringhe
 * const categoriesFilter = ref(createArrayFilterData<string>('categories').in());
 *
 * @example
 * // Con numeri e configurazione completa
 * const idsFilter = ref(
 *   createArrayFilterData<number>('productIds')
 *     .in()
 *     .initialValue([1, 2, 3])
 *     .setSeparator(',')
 * );
 */
export function createArrayFilterData<T extends string | number = string>(
	propertyName: string,
	_type?: IdConstructor,
): ArrayFilterFactory<T> {
	// Sanitizza nome proprietà per query string
	const sanitizedName = propertyName.replace(/[.[\\]]/g, '_').toLowerCase();

	// Oggetto combinato con dati e metodi
	const filter: ArrayFilterFactory<T> = {
		propertyName,
		value: null,
		defaultValue: null,
		queryPropertyName: sanitizedName,
		searchOperator: 'NOOP',
		updateQueryOnChange: false,
		separator: '*',

		initialValue(value: T[] | null): ArrayFilterFactory<T> {
			filter.value = value;
			return filter;
		},

		default(value: T[] | null): ArrayFilterFactory<T> {
			filter.defaultValue = value;
			return filter;
		},

		updateQueryOnFilterChange(value: boolean): ArrayFilterFactory<T> {
			filter.updateQueryOnChange = value;
			return filter;
		},

		setQueryPropertyName(value: string): ArrayFilterFactory<T> {
			filter.queryPropertyName = value;
			return filter;
		},

		in(): ArrayFilterFactory<T> {
			filter.searchOperator = 'IN';
			return filter;
		},

		contains(): ArrayFilterFactory<T> {
			filter.searchOperator = 'CONTAINS';
			return filter;
		},

		setSeparator(separator: string): ArrayFilterFactory<T> {
			filter.separator = separator;
			return filter;
		},

		addValue(value: T): ArrayFilterFactory<T> {
			if (!filter.value) {
				filter.value = [];
			}
			if (!filter.value.includes(value)) {
				filter.value.push(value);
			}
			return filter;
		},

		removeValue(value: T): ArrayFilterFactory<T> {
			if (filter.value) {
				const index = filter.value.indexOf(value);
				if (index >= 0) {
					filter.value.splice(index, 1);
				}
			}
			return filter;
		},
	};

	return filter;
}
